import { Box, Card, TextField, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

type Props = {
  title: string;
  value: string;
  canEdit: boolean;
  onSave: (next: string) => Promise<void>;
  placeholder?: string;
};

type SaveState = 'idle' | 'saving' | 'saved' | 'error';

/** A titled narrative block. Read-only renders plain text; editable renders a
 * multiline field that flushes on blur (one PATCH per intent), mirroring the
 * finding-row save-on-blur pattern. */
export const CfiReportNarrative = ({ title, value, canEdit, onSave, placeholder }: Props) => {
  const [draft, setDraft] = useState(value);
  const [saveState, setSaveState] = useState<SaveState>('idle');

  useEffect(() => {
    setDraft(value);
  }, [value]);

  const handleBlur = async () => {
    // Guard against overlapping blur-saves: a second PATCH must not race the
    // first and persist stale content out of order.
    if (saveState === 'saving') return;
    if (draft === value) return;
    setSaveState('saving');
    try {
      await onSave(draft);
      setSaveState('saved');
      setTimeout(() => setSaveState('idle'), 1500);
    } catch (err) {
      console.error('CFI narrative save failed', err);
      setSaveState('error');
    }
  };

  if (!canEdit) {
    if (!value) return null;
    return (
      <Card variant="outlined" sx={{ p: 2, mb: 3 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>
          {title}
        </Typography>
        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
          {value}
        </Typography>
      </Card>
    );
  }

  return (
    <Box sx={{ my: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 0.5 }}>
        {title}
      </Typography>
      <TextField
        multiline
        minRows={2}
        fullWidth
        size="small"
        disabled={saveState === 'saving'}
        value={draft}
        placeholder={placeholder}
        aria-label={title}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={handleBlur}
        helperText="Clearing the text reverts to the AI-generated version."
      />
      {saveState === 'saving' ? (
        <Typography variant="caption" color="text.secondary">
          Saving…
        </Typography>
      ) : null}
      {saveState === 'saved' ? (
        <Typography variant="caption" color="success.main">
          Saved ✓
        </Typography>
      ) : null}
      {saveState === 'error' ? (
        <Typography variant="caption" color="error.main">
          Failed — edit again to retry
        </Typography>
      ) : null}
    </Box>
  );
};
